import { z } from 'zod/v4';

/**
 * One `indexes` / `defaultIndexes` entry — a free-form JSON blob written by
 * risk-gross create/update (`title`, `indexValue`, `link`, `sourceType`, and
 * optionally `id`, `description`, `year`, `retracedScore`). Kept loosely typed,
 * same treatment as `RiskLandscapeIndexSchema` — the legacy handler only ever
 * passes it through unchanged.
 */
export const RiskGrossIndexSchema = z.record(z.string(), z.unknown());

/**
 * Body of `POST /risk-analysis/risk-gross`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-gross.js` →
 * `create.body`.
 *
 * `id`, `validFrom` and `validUpto` are deliberately absent: the legacy schema
 * accepted all three, but the handler only ever used `riskId`, `countryCode`,
 * `grossValue` and `indexes`.
 *
 * - `validFrom`/`validUpto` were accepted-but-ignored noise — the legacy handler
 *   always stamped `NOW()` server-side regardless of what the body carried
 *   (pinned by the legacy integration test), so this contract stamps the same
 *   way and never reads a caller-supplied value.
 * - `id` was a real bug, not noise: the legacy INSERT did
 *   `createQuery({id: generatedId, ...body})`, and object-spread let a
 *   caller-supplied `body.id` silently override the generated primary key —
 *   spoofable, and it also broke the response (the handler still re-fetched by
 *   the *generated* id, so a spoofed create returned 201 with an empty body).
 *   No caller ever sent it. Fixed here by never accepting it: the id is always
 *   server-generated.
 */
export const CreateRiskGrossBodySchema = z.object({
  riskId: z.string().min(1).max(36),
  countryCode: z.string().min(1).max(4),
  grossValue: z.number().min(1).max(5).nullable(),
  indexes: z.array(RiskGrossIndexSchema).optional(),
});

export type CreateRiskGrossBody = z.infer<typeof CreateRiskGrossBodySchema>;

/** Params of `PATCH /risk-analysis/risk-gross/:id`. */
export const UpdateRiskGrossParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type UpdateRiskGrossParams = z.infer<typeof UpdateRiskGrossParamsSchema>;

/**
 * Body of `PATCH /risk-analysis/risk-gross/:id`, ported from `update.body` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-gross.js`.
 *
 * Only `grossValue` and `indexes` — the two fields the legacy handler read. The
 * legacy schema accepted every risk-gross property, and both frontends still
 * send `riskId` and `countryCode`, but the handler always took those from the
 * existing row: an update writes a new version of the same risk × country, it
 * never moves one. They are stripped here the same way AJV stripped them.
 */
export const UpdateRiskGrossBodySchema = z.object({
  grossValue: z.number().min(1).max(5).nullable(),
  indexes: z.array(RiskGrossIndexSchema).optional(),
});

export type UpdateRiskGrossBody = z.infer<typeof UpdateRiskGrossBodySchema>;

/**
 * A risk_gross row enriched with its risk's default indexes and its country's
 * metadata, ported from `buildRiskGross` in
 * `2025-06/risk-analysis-service/handlers/v3/risk-gross/helpers.js`. Shared by
 * the create and update responses (`getRiskGrossById` re-fetch) — there is no standalone
 * `GET /risk-analysis/risk-gross/:id` route; nothing calls it.
 */
export const RiskGrossResourceSchema = z.object({
  id: z.string(),
  riskId: z.string().nullable(),
  countryCode: z.string().nullable(),
  grossValue: z.number().nullable(),
  validFrom: z.string().nullable(),
  validUpto: z.string().nullable(),
  indexes: z.array(RiskGrossIndexSchema).nullable(),
  defaultIndexes: z.array(RiskGrossIndexSchema).nullable(),
  countryName: z.string().nullable(),
  center: z.unknown(),
  countryCodeAlpha3: z.string().nullable(),
});

export type RiskGrossResource = z.infer<typeof RiskGrossResourceSchema>;

const LIST_RISK_GROSS_DEFAULT_PAGE_SIZE = 15;
const LIST_RISK_GROSS_MAX_PAGE_SIZE = 500;

/**
 * Querystring of `GET /risk-analysis/risk-gross`, ported from the legacy
 * `list.querystring` in `request/risk-gross.js`. The default page stays 15;
 * `count` starts at 1 (legacy `0` silently became 15).
 *
 * `sort` is `<field>:<ASC|DESC>`. The legacy querystring never declared it, so
 * AJV stripped the `sort` both frontends send and the order was always
 * `validFrom DESC`; it is honoured now for the fields in `SORT_COLUMNS`
 * (`list.ts`). Any other field falls back to the default order.
 */
export const ListRiskGrossQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(1)
    .max(LIST_RISK_GROSS_MAX_PAGE_SIZE)
    .default(LIST_RISK_GROSS_DEFAULT_PAGE_SIZE),
  sort: z
    .string()
    .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
    .optional(),
  /** Stringified JSON — see `FILTER_COLUMNS` in `list.ts`. */
  filter: z.string().max(512).optional(),
});

export type ListRiskGrossQuery = z.infer<typeof ListRiskGrossQuerySchema>;

/**
 * One row of `GET /risk-analysis/risk-gross` — a gross index with its risk's
 * institute/title and its country's name.
 *
 * The fields both frontends read. The legacy response definition was the
 * malformed `{body: {...}}` shape, so nothing was stripped and every selected
 * column — the per-row `count`, `center`, `section`, `description`,
 * `countryCodeAlpha3` — went out; none of those are read.
 */
export const RiskGrossListItemSchema = z.object({
  id: z.string(),
  riskId: z.string(),
  institute: z.string().nullable(),
  title: z.string().nullable(),
  countryCode: z.string(),
  countryName: z.string().nullable(),
  grossValue: z.number().nullable(),
  validFrom: z.string().nullable(),
  validUpto: z.string().nullable(),
  indexes: z.array(RiskGrossIndexSchema).nullable(),
});

export type RiskGrossListItem = z.infer<typeof RiskGrossListItemSchema>;
